// ============================================================================
// Dashboard Router
// Feature: Role-based dashboard routing
// Created: 2026-01-22
// Description: Routes to appropriate dashboard based on user role
// ============================================================================

import React from 'react';
import { useAuth } from '../../modules/backup/hooks/useAuth';
import AdminDashboard from './AdminDashboard';
import OperatorDashboard from './OperatorDashboard';
import ManagerDashboard from './ManagerDashboard';
import UserDashboard from './UserDashboard';

type UserRole = 'Admin' | 'Operator' | 'Manager' | 'Viewer' | 'User';

const ROLE_MAP: Record<string, UserRole> = {
  // Spec 285: the Dashboard is identical for Admin and SuperAdmin (owner). Without this key a
  // SuperAdmin fell through to the Viewer-level UserDashboard.
  superadmin: 'Admin',
  admin: 'Admin',
  operator: 'Operator',
  manager: 'Manager',
  viewer: 'Viewer',
  user: 'User',
};

export const DashboardRouter: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading || !user) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4' />
          <p className='text-gray-600 dark:text-gray-400'>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const role: UserRole = ROLE_MAP[String(user.role).toLowerCase()] || 'Viewer';

  switch (role === 'User' ? 'Viewer' : role) {
    case 'Admin':    return <AdminDashboard />;
    case 'Operator': return <OperatorDashboard />;
    case 'Manager':  return <ManagerDashboard />;
    default:         return <UserDashboard />;
  }
};

export default DashboardRouter;
